import React, { useState, useRef } from "react";
import "./contentpage.css";
import FloatingPageNav from "../components/FloatingPageNav";

import Question from "../components/Question";
import LandingSection from "./LandingSection";

function IslamicTradePage12() {
  const contentRef = useRef(null);
   const links = {
    prev: "/islamic-trade-page-11", 
    index: "/index",                 
    next: "/islamic-trade-page-13", 
  };
  const titleText = "Rulings on Mortgage (Rahn) in Islam";

  const questions = [
    {
      text: "1. What is the basic definition of Rahn (mortgage) in Islamic finance?)?",
      options: [
        {
          value: "A",
          label: "A.  A loan that includes interest.",
        },
        {
          value: "B",
          label: "B. A gift given to the borrower",
        },
        {
          value: "C",
          label: "C. A pledge of an asset to secure a loan or debt.",
        },
        {
          value: "D",
          label: "D. A form of partnership in trade.",
        },
      ],
      correct: "C",
    },
    {
      text: "2.  Which of the following is not a valid form of collateral (rahn) in Islam??",
      options: [
        { value: "A", label: "A. Tangible property (‘Ayn)." },
        { value: "B", label: "B. Usufruct or benefit (Manfa‘ah)." },
        { value: "C", label: "C. Debt owed to the borrower (Dayn)." },
        { value: "D", label: "D. A verbal promise of repayment." },
      ],
      correct: "D",
    },
    {
      text: "3. If the mortgaged item produces a benefit (e.g., a goat gives milk), who is responsible for its upkeep?",
      options: [
        { value: "A", label: "A. The lender (mortgagee)." },
        { value: "B", label: "B. The borrower (mortgagor)" },
        { value: "C", label: "C. The government" },
        { value: "D", label: "D. The buyer of the item" },
      ],
      correct: "B",
    },
    {
      text: "4. If a pledged asset is worth more than the loan and must be sold due to default, what happens to the surplus value??",
      options: [
        { value: "A", label: "A. It belongs to the lender as a reward" },
        {
          value: "B",
          label: "B. It must be split between borrower and lender",
        },
        { value: "C", label: "C.  It must be returned to the borrower" },
        { value: "D", label: "D. The buyer of the item" },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={12}
        timerMinutes={60}
        currentLevel={1}
      />

      <p className="content" ref={contentRef}>
        <b>Foundations of Rahn</b>
        <br />
        <br />
        Welcome to <b>Day 12</b>
        ! Today, we delve into the Islamic understanding of mortgage, or rahn,
        an essential financial tool that provides security in lending and
        borrowing arrangements. Rooted in the Qur’an and demonstrated through
        the practice of the Prophet Muhammad (peace be upon him), the concept of
        rahn reflects Islam’s balance between trust and accountability in
        financial dealings. You will explore the various forms a mortgage can
        take, whether tangible assets, usage rights, or even debts and
        understand the rulings that ensure fairness to both lender and borrower.
        This lesson will help you appreciate how Islamic law safeguards
        financial agreements while upholding the principles of justice,
        transparency, and mutual respect.
        <br />
        <br />
        The concept of Rahn, or mortgage, occupies a significant place in
        Islamic financial ethics and jurisprudence. Rooted deeply in the Qur’an
        and the Sunnah, rahn serves as an instrument that balances trust and
        accountability within economic transactions. In essence, rahn refers to
        a form of secured lending in which an item of value is pledged by a
        borrower to a lender as collateral against a debt or loan. If the
        borrower defaults on their obligation, the lender possesses the right to
        recover the debt by selling the pledged item. However, this right is
        bound by strict principles of fairness and justice, ensuring that no
        party exploits the other in the process.
        <br />
        The Qur’an establishes the legitimacy of rahn in a concise but profound
        manner. In Surah Al-Baqarah (2:283), Allah says, <br />
        If you are on a journey and a scribe cannot be found, then a security
        can be taken. If you trust one another, then ˹there is no need for a
        security, but˺ the debtor should honour this trust ˹by repaying the
        debt˺—and let them fear Allah, their Lord. And do not conceal the
        testimony, for whoever conceals it, their hearts are indeed sinful. And
        Allah ˹fully˺ knows what you do."{" "}
        <a href="https://quran.com/2/283" target="blank">
          (Surah Al Baqarah 283)
        </a>
        <br />
        This verse was revealed in the context of transactions where
        documentation or witnesses were not available, illustrating the
        permissibility of taking a form of security to protect both parties in a
        contract. The divine instruction does not merely allow rahn but subtly
        promotes accountability in all dealings. The underlying message is that
        trust, while essential, must be complemented with prudent safeguards to
        ensure justice.
        <br />
        The practice of rahn is also validated through the Sunnah. The Prophet
        Muhammad (peace and blessings be upon him) himself pledged his armor as
        collateral to a Jewish merchant in exchange for barley to sustain his
        household. This authentic narration establishes two critical dimensions
        of rahn. Firstly, it confirms the permissibility of mortgaging even with
        non-Muslims, demonstrating Islam’s emphasis on fairness and integrity in
        transactions with all people. Secondly, it reveals that even the
        Prophet, the most trustworthy of men, endorsed practical measures of
        financial security, signifying that reliance on Allah does not negate
        worldly prudence.
        <br />
        Rahn, therefore, is not a tool of exploitation but a mechanism to
        preserve mutual trust. It ensures that the lender feels secure in
        extending financial assistance while the borrower receives the support
        they need without resorting to unethical or interest-based systems. The
        transaction, when conducted within Shariah boundaries, strengthens
        social bonds rather than weakening them. The essence of rahn lies in
        mutual respect, transparency, and the balance between spiritual
        integrity and material responsibility.
        <br />
        In Islamic law, every item that can be lawfully sold can also be
        mortgaged. This principle arises because rahn represents a conditional
        transfer of possession for the purpose of security. Hence, the pledged
        asset must be capable of ownership transfer. The valuation of a
        mortgaged item, however, need not correspond precisely with the debt
        amount. The Qur’an and Sunnah leave this to the discretion of the
        contracting parties. What matters is that the lender can recover only
        the amount owed from the pledged asset and nothing more. This rule
        prevents any unjust enrichment and maintains the sanctity of the
        transaction.
        <br />
        For example, if a borrower takes a loan of one Bitcoin (BTC) and pledges
        jewelry worth the same value, the arrangement is fair and balanced.
        Should the borrower default, the lender may sell the jewelry to recover
        exactly one Bitcoin’s worth. Conversely, if the collateral is worth less
        than the debt, say, 1.5 BTC pledged for a 2 BTC loan, the lender may
        sell the asset but must seek the remaining amount through lawful means.
        And if the collateral exceeds the loan, such as pledging a house valued
        at 5 BTC for a 3 BTC loan, the lender is entitled only to the repayment
        of 3 BTC upon liquidation, returning any surplus to the borrower.
        <br />
        This system promotes justice by ensuring that neither party gains at the
        other’s expense. The lender is protected from loss, while the borrower’s
        rights over surplus value remain intact. Thus, the wisdom of rahn in
        Islam lies in establishing equilibrium between financial security and
        ethical responsibility. It stands as a reminder that wealth must
        circulate through honesty and care, reflecting the Qur’anic command:
        <br />
        “Do not consume one another’s wealth unjustly, nor deliberately bribe
        authorities in order to devour a portion of others’ property, knowing
        that it is a sin.”{" "}
        <a href="https://quran.com/2/188" target="blank">
          (Surah Al-Baqarah 188)
        </a>
        <br />
        <br />
        <b>Forms and Applications of Rahn in Islamic Jurisprudence</b>
        <br />
        <br />
        In Islamic jurisprudence, rahn can manifest in various forms, reflecting
        the flexibility of Shariah in accommodating diverse financial needs.
        These forms include ‘Ayn (tangible property), Manfa‘ah (usufruct or
        benefit), and Dayn (debt). Each form adheres to distinct rules that
        preserve the essence of justice and fairness.
        <br />
        The most common type of mortgage is the ‘Ayn, or tangible property. ‘Ayn
        refers to a physical object or asset that can be
        lawfully owned and traded. Examples include houses, land, vehicles,
        livestock, or jewelry. When such an asset is pledged as collateral, the
        borrower continues to own it, but the lender holds a right of security
        over it. If the borrower fails to fulfill the obligation, the lender may
        sell the item to recover the debt. However, any amount received beyond
        the debt must be returned to the borrower. This ensures that the lender
        benefits only to the extent of their due, maintaining the transaction’s
        ethical integrity.
        <br />
      
        The second form, <b>Manfa‘ah</b>, refers to the benefit or usage rights
        derived from an asset rather than the asset itself. In this arrangement,
        the borrower pledges the usufruct, the right to use something as
        collateral. For instance, a borrower may pledge the right to use a
        rental property as security for a loan. The lender, in such a case, does
        not own the property but may utilize or lease it for a specified
        duration. This form of rahn is valid only if the duration, nature, and
        value of the usufruct are clearly defined, as ambiguity could lead to
        disputes or unjust gain. The pledge of manfa‘ah aligns with Islamic
        legal objectives (maqasid al-shariah) by providing flexibility in
        transactions while avoiding exploitation.
        <br />
        The third form, <b>Dayn</b>, represents debts or receivables. It
        involves pledging a financial claim owed to the borrower by another
        party as security for a new loan. This means that the borrower assigns
        the right to collect a debt from a third party to the lender, who can
        recover it if the borrower defaults. The key requirement is that the
        pledged debt must be authentic, well-documented, and free from
        uncertainty. This form of rahn demonstrates Islam’s capacity to adapt to
        complex financial realities without compromising on principles of
        fairness.
        <br />A practical example helps to illuminate this diversity. Consider a
        farmer who borrows $2,000 and pledges his tractor as collateral. This is
        rahn through ʿayn. Another borrower, who allows the lender to use their
        car for six months in exchange for a loan, engages in rahn through
        manfa‘ah. Meanwhile, a merchant who pledges an invoice of $5,000 owed to
        him by a customer to secure a new $3,000 loan is practicing rahn through
        dayn. These examples show that Islamic law recognizes both tangible and
        intangible forms of security, making rahn a versatile and humane
        mechanism suited for diverse contexts.
        <br />
        In all cases, certain key principles govern the process. The pledged
        asset must be owned by the borrower, free from any ownership disputes.
        Transparency is essential both parties must understand the value, type,
        and conditions of the mortgage. Any ambiguity (gharar) could render the
        contract invalid.
        <br />
        <br />
        <b>Timing, Ownership, and Special Applications of Rahn</b>
        <br />
        <br />
        One of the strengths of Islamic jurisprudence lies in its flexibility,
        and the timing of rahn reflects this adaptability. A mortgage may be
        executed either before or after the primary contract, such as a loan or
        sale. This dual possibility facilitates ease and practicality in
        financial dealings. An individual may first pledge an asset and then
        formalize the loan agreement, or they may enter into the loan and
        subsequently provide collateral. Both scenarios are valid, provided the
        conditions of the rahn are transparent, mutually agreed upon, and free
        from deception. The underlying purpose is to ensure fairness and prevent
        hardship, aligning with the maqasid al-shariah, the objectives of
        Islamic law, which seek to promote justice and remove unnecessary
        difficulties.
        <br />A question often arises regarding the permissibility of mortgaging
        jointly owned property. In Islam, it is indeed permissible to mortgage
        an undivided share in a property jointly owned by multiple individuals.
        The rationale behind this ruling stems from the principle that if it is
        permissible to sell a share in jointly owned property, then it is
        likewise permissible to pledge it as collateral. This principle is
        reinforced by the hadith narrated by Jabir ibn Abdullah (may Allah be
        pleased with him), who reported that the Messenger of Allah (peace be
        upon him) gave a verdict regarding shuf‘a (the right of preemption) in
        every undivided joint property. However, once boundaries and pathways
        are demarcated, the right of preemption no longer applies. This implies
        that undivided ownership does not bar an individual from undertaking
        financial actions such as selling or mortgaging their portion, provided
        the rights of co-owners are not infringed upon.
        <br />
        Islamic law pays meticulous attention to the treatment of the mortgaged
        item and any additions or benefits it may yield. If a mortgaged goat
        gives birth, the offspring automatically becomes part of the mortgage.
        If a mortgaged car is damaged and compensation is received from the
        liable party, that compensation is also considered part of the mortgage.
        These rulings ensure that the value of the collateral remains consistent
        with the secured obligation. Maintenance expenses, however, are the
        responsibility of the mortgagor, the borrower who owns the asset. This
        includes repair costs, upkeep, and taxes associated with the property.
        The rationale is that ownership remains with the mortgagor even though
        the possession may be temporarily restricted. <br />
        The Prophet Muhammad (peace and blessings be upon him) provided clear
        guidance on this matter in a hadith recorded in 
        <a href="https://sunnah.com/bukhari:2512" target="blank">
          Sahih al-Bukhari (2512):
          <br />
        </a>
        “The mortgaged animal can be used for riding as long as it is fed and
        the milk of the milch animal can be drunk according to what one spend on
        it. The one who rides the animal or drinks its milk should provide the
        expenditures.” <br />
        This profound teaching establishes a principle of proportionality. The
        lender may derive limited use or benefit from the mortgaged asset only
        to the extent that it compensates them for maintenance expenses.
        Anything beyond this would be considered unjust enrichment and could
        potentially amount to riba (usury).
        <br />
        Another essential principle is that a mortgage remains fully in effect
        until the debt is completely repaid. Partial repayment of the loan does
        not release a corresponding portion of the mortgaged asset. The
        collateral remains under security until the obligation is fulfilled in
        its entirety. This ensures clarity, avoids confusion, and maintains the
        lender’s protection throughout the contract term.
        <br />
        <br />
        <b>Rahn as a Pillar of Fairness and Trust in Islamic Finance</b>
        <br />
        <br />
        The concept of rahn embodies Islam’s broader economic philosophy, which
        seeks to foster trust and cooperation while preventing exploitation and
        injustice. In conventional mortgage systems, it is common for the
        lender, typically a bank, to gain control over the mortgaged property,
        often earning interest or profit beyond the principal loan. Such
        structures inherently conflict with Islamic ethics, as they commodify
        debt and burden borrowers. Rahn, by contrast, ensures that security
        serves only as protection, not as a means of profit. <br />
        Rahn, or mortgage, refers to an arrangement where a borrower pledges an
        asset as collateral to secure a loan or debt. This arrangement ensures
        that the lender has a form of security in case the borrower is unable to
        fulfill their financial obligations. <br />
        Through rahn, Islam bridges the gap between personal trust and legal
        formality. It recognizes human fallibility and the potential for
        financial default, but it addresses this risk through transparent,
        mutually agreed-upon measures rather than through interest-based
        penalties. The emphasis is on justice, not punishment. The Prophet
        Muhammad (peace be upon him) repeatedly emphasized fairness in
        transactions, teaching that both the lender and borrower bear moral
        obligations. The lender must not exploit the borrower’s vulnerability,
        while the borrower must honor their commitment with sincerity and
        punctuality.
        <br />
        Rahn also plays a vital role in microfinance and rural development in
        Muslim societies. Farmers, small traders, and artisans can access
        capital by pledging their tools, livestock, or produce as collateral.
        Such arrangements enable economic empowerment without entangling
        individuals in cycles of interest-bearing debt. Furthermore, by ensuring
        that any surplus proceeds from the sale of collateral are returned to
        the borrower, rahn reinforces the ethical obligation to protect human
        dignity and wealth.
        <br />
        Beyond financial institutions, the moral principles of rahn nurture
        societal trust. It encourages Muslims to engage in financial dealings
        confidently, knowing that Islam provides mechanisms to secure their
        rights. It deters deceit and carelessness, reminding all parties that
        financial honesty is an act of worship. As the Prophet (peace be upon
        him) said, “The truthful and trustworthy merchant will be with the
        Prophets, the truthful, and the martyrs.”{" "}
        <a href="https://sunnah.com/tirmidhi:1209" target="blank">
          (Sunan At-Tirmidhi 1209)
        </a>
        <br />
        This noble hadith illustrates that financial integrity elevates one’s
        spiritual rank, connecting economic ethics directly with faith.
        <br />
        Rahn in Islam is more than a legal contract, it is a reflection of divine
        wisdom in managing worldly affairs. It harmonizes material
        responsibility with moral accountability, ensuring that wealth
        circulates fairly and securely. By embedding justice and compassion into
        financial structures, Islam prevents the exploitation that arises from
        interest-based systems. Whether through tangible assets, usage rights,
        or debts, the principle of rahn offers a humane alternative to modern
        financial practices, emphasizing that true prosperity lies in ethical
        cooperation and mutual respect.
        <br />
        The study of rahn, therefore, is not merely an exploration of an ancient
        financial tool but a lesson in how faith and finance can coexist without
        contradiction. It reminds us that the ultimate goal of Islamic economics
        is not merely to accumulate wealth but to establish justice (adl) and
        uphold trust (amanah) among people. Through its balance of security and
        fairness, rahn stands as a timeless embodiment of Islam’s call to
        integrity in all dealings, an enduring guide for believers navigating
        the complexities of modern finance while remaining steadfast upon the
        principles of divine law.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        Today’s lesson explored the concept of Rahn (mortgage) in Islam,
        emphasizing its permissibility as a form of securing debts through
        pledged assets. We learned that mortgages can take the form of tangible
        property (‘ayn), benefit or usage rights (manfa‘ah), and even debts
        (dayn), each with its own conditions and applications. The lesson also
        clarified that the pledged asset doesn’t have to match the loan’s value
        exactly, but only the owed amount can be recovered by the lender.
        Important principles such as ownership, transparency, and the
        prohibition of exploitation were highlighted to ensure fairness and
        Shariah compliance. We also covered how surplus from a mortgaged item,
        compensation for damage, and maintenance responsibilities are handled,
        as well as the rulings on jointly owned property and the timing of
        pledging a mortgage. Altogether, this lesson reinforced the role of rahn
        as a practical and ethical tool in Islamic financial dealings.
        <br />
        <br />
        <b>Coming up next</b>
        <br />
        <br />
        Next we delve into Rulings on Guarantee (Dhaman) and Patronage
        (Kafalah). This lesson explores how Islam encourages mutual financial
        responsibility and social solidarity through mechanisms like Dhaman,
        where one takes on another’s debt obligation, and Kafalah, where a
        person guarantees someone’s presence or commitment. We will study their
        permissible forms, conditions, and the ethical boundaries that govern
        these contracts. Through examples from the Sunnah and foundational
        Quranic principles, we will see how these tools foster justice,
        cooperation, and accountability in both financial and legal dealings.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

       <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default IslamicTradePage12;
